import type { Dtos } from "@inrev/common";
import { formatToDollar } from "@inrev/common";
import { Option } from "@inrev/core";
import { FC } from "react";

type Report = Dtos.Admin.SuretyAccount.Get.Response["companies"][number]["creditReports"][number];

type DerogCardProps = {
	title: string;
	count: number | null | undefined;
	balance?: number | null | undefined;
};

const DerogCard: FC<DerogCardProps> = ({ title, count, balance }) => {
	const isKnown = Option.isSome(count);
	const isClear = isKnown && count === 0;

	return (
		<div
			className="relative flex flex-col rounded-xl border border-bc-default bg-surface-base p-4 shadow-sm"
			data-testid={`company_credit_public_${title.toLowerCase().replace(/\s+/g, "_")}`}
		>
			{isKnown && (
				<div className="absolute right-3 top-3 text-[11px] font-medium uppercase tracking-wide">
					<span className={isClear ? "text-confirm" : "text-danger"}>
						{isClear ? "Clear" : "On file"}
					</span>
				</div>
			)}
			<div className="text-tertiary text-[11px] font-medium uppercase tracking-wide">{title}</div>
			<div className="mt-2 text-[28px] font-semibold leading-none text-primary">
				{isKnown ? count : "—"}
			</div>
			<div className="text-tertiary mt-1 text-[13px]">
				{isKnown ? (isClear ? "No records" : "See summary") : "No data"}
			</div>
			{Option.isSome(balance) && balance > 0 && (
				<div className="text-secondary mt-1 text-[12px]">{formatToDollar(balance, true)}</div>
			)}
		</div>
	);
};

type PublicRecordsPanelProps = {
	report: Report;
};

export const PublicRecordsPanel: FC<PublicRecordsPanelProps> = ({ report }) => {
	const elements = report.creditProfile?.experianExecutiveElements;

	const allCounts = [
		elements?.bankruptcyCount,
		elements?.taxLienCount,
		elements?.judgmentCount,
		elements?.collectionCount,
	];
	const hasAny = allCounts.some((n) => Option.isSome(n) && n > 0);
	const allClear = !hasAny && allCounts.every((n) => n === 0);

	return (
		<div
			className="space-y-3 border-b border-bc-default pb-5"
			data-amp-mask
			data-testid="company_credit_public_records_panel"
		>
			<div className="flex flex-wrap items-center justify-between gap-2">
				<h3 className="text-[15px] font-medium text-primary">Public Records & Derogatory</h3>
				<div className="flex items-center gap-2 text-[13px]">
					{allClear && (
						<>
							<span
								className="h-2 w-2 shrink-0 rounded-full bg-confirm"
								aria-hidden
								data-testid="company_credit_no_public_records_dot"
							/>
							<span className="text-confirm" data-testid="company_credit_no_public_records_label">
								No public records
							</span>
						</>
					)}
					{hasAny && (
						<>
							<span className="h-2 w-2 shrink-0 rounded-full bg-danger" aria-hidden />
							<span className="text-danger" data-testid="company_credit_public_records_on_file">
								Public records on file
							</span>
						</>
					)}
				</div>
			</div>
			<div className="grid grid-cols-2 gap-3 md:grid-cols-4">
				<DerogCard title="Bankruptcies" count={elements?.bankruptcyCount} />
				<DerogCard
					title="Tax Liens"
					count={elements?.taxLienCount}
					balance={elements?.lienBalance ?? report.creditProfile?.lienBalance}
				/>
				<DerogCard
					title="Judgments"
					count={elements?.judgmentCount}
					balance={elements?.judgmentBalance ?? report.creditProfile?.judgmentBalance}
				/>
				<DerogCard title="Collections" count={elements?.collectionCount} />
			</div>
		</div>
	);
};
